<template>
  <div>
    <v-container fluid>
      <Govem_bar />
      <base-material-card
        icon="mdi-calculator"
        title="คำนวณค่าตอบแทนพนักงานราชการ"
        class="px-5 py-3"
      >
        <!-- ===== ALERT HEADER ===== -->
        <v-row class="mb-4">
          <v-col cols="12">
            <v-alert
              type="info"
              prominent
              outlined
              border="left"
              colored-border
              elevation="2"
              icon="mdi-calculator-variant-outline"
            >
              <div class="text-subtitle-1 font-weight-bold">
                ระบบคำนวณการเลื่อนค่าตอบแทนพนักงานราชการทั่วไป
              </div>
              <div class="text-body-2 mt-1">
                ตามหลักเกณฑ์
                <strong>ประกาศสำนักงานคณะกรรมการการอาชีวศึกษา พ.ศ. 2569</strong>
                — มีผลวันที่ <strong>1 ตุลาคม</strong> ของทุกปี
              </div>
              <v-divider class="my-2" />
              <v-row dense class="caption">
                <v-col cols="12" md="4">
                  <v-icon small color="info">mdi-check-circle</v-icon>
                  วงเงินรวม <strong>ไม่เกิน 4%</strong> ของฐานค่าตอบแทน ณ 1 ก.ย.
                </v-col>
                <v-col cols="12" md="4">
                  <v-icon small color="info">mdi-check-circle</v-icon>
                  รายบุคคล <strong>ไม่เกิน 6%</strong> ของฐานค่าตอบแทน
                </v-col>
                <v-col cols="12" md="4">
                  <v-icon small color="info">mdi-check-circle</v-icon>
                  ปัดเศษ <strong>ไม่ถึง 10 บาท → ขึ้นเป็น 10 บาท</strong> (ข้อ
                  1(6))
                </v-col>
              </v-row>
            </v-alert>
          </v-col>
        </v-row>

        <!-- ===== STEP 1: ตั้งค่า ===== -->
        <v-card outlined class="mb-4">
          <v-card-title
            class="subtitle-1 font-weight-bold grey lighten-4 py-2 px-4"
          >
            <v-avatar color="info darken-3" size="26" class="mr-2">
              <span class="white--text caption font-weight-bold">1</span>
            </v-avatar>
            ตั้งค่าการคำนวณ
          </v-card-title>
          <v-card-text class="pt-4">
            <v-row dense>
              <v-col cols="12" md="3">
                <v-select
                  v-model="selectedYear"
                  :items="years"
                  label="ปีงบประมาณ *"
                  outlined
                  dense
                  prepend-inner-icon="mdi-calendar-range"
                  hide-details="auto"
                  @change="onYearChange"
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model.number="compensationPercentage"
                  label="วงเงินรวม (%)"
                  outlined
                  dense
                  type="number"
                  step="0.5"
                  min="0"
                  max="4"
                  suffix="%"
                  readonly
                  hint="สูงสุด 4% ข้อ 1(3)"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model.number="maxAllowedPercentage"
                  label="สูงสุดรายบุคคล (%)"
                  outlined
                  dense
                  type="number"
                  step="0.5"
                  min="0"
                  max="6"
                  suffix="%"
                  readonly
                  hint="สูงสุด 6% ข้อ 1(2)"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-select
                  v-model="assessmentRound"
                  :items="[
                    { text: 'รอบที่ 1', value: 1 },
                    { text: 'รอบที่ 2', value: 2 }
                  ]"
                  label="รอบประเมิน"
                  outlined
                  dense
                  hide-details="auto"
                  hint="รอบที่ใช้คำนวณ"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" md="3" class="d-flex align-end pb-5">
                <v-btn
                  color="info darken-3"
                  dark
                  block
                  large
                  :loading="loading"
                  @click="loadAllData"
                  :disabled="!selectedYear"
                >
                  <v-icon left>mdi-database-search-outline</v-icon>
                  โหลดข้อมูล
                </v-btn>
              </v-col>
            </v-row>

            <!-- เกณฑ์สิทธิ์ -->
            <v-expansion-panels flat class="mt-2">
              <v-expansion-panel>
                <v-expansion-panel-header class="caption grey--text py-1 px-2">
                  <v-icon small class="mr-1">mdi-shield-check-outline</v-icon>
                  เงื่อนไขการมีสิทธิ์รับการเลื่อนค่าตอบแทน (คลิกเพื่อดู)
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense class="mt-1">
                    <v-col cols="12" md="6">
                      <v-simple-table dense>
                        <tbody>
                          <tr>
                            <td class="caption py-1">
                              <v-icon x-small color="success">mdi-check</v-icon>
                              ปฏิบัติงานในรอบปีงบประมาณ ≥ 8 เดือน
                            </td>
                          </tr>
                          <tr>
                            <td class="caption py-1">
                              <v-icon x-small color="success">mdi-check</v-icon>
                              ผลการปฏิบัติงาน ≥ ระดับดี (คะแนน ≥ 70)
                            </td>
                          </tr>
                          <tr>
                            <td class="caption py-1">
                              <v-icon x-small color="error">mdi-close</v-icon>
                              ไม่ขาดราชการในรอบปี (ข้อ 1(7))
                            </td>
                          </tr>
                          <tr>
                            <td class="caption py-1">
                              <v-icon x-small color="error">mdi-close</v-icon>
                              วันลาไม่เกิน 45 วัน ไม่รวม: ลาคลอด≤90วัน, ลาทหาร,
                              ลาพักผ่อน (ข้อ 1(8))
                            </td>
                          </tr>
                          <tr>
                            <td class="caption py-1">
                              <v-icon x-small color="error">mdi-close</v-icon>
                              ลากิจ/ลาป่วย ≤ 14 ครั้ง หรือมาสาย ≤ 18 ครั้ง (ข้อ
                              1(9))
                            </td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div
                        class="caption font-weight-bold mb-1 info--text text--darken-2"
                      >
                        เกณฑ์คะแนน → % เลื่อน
                      </div>
                      <v-simple-table dense>
                        <thead>
                          <tr>
                            <th class="caption">ระดับ</th>
                            <th class="caption">คะแนน</th>
                            <th class="caption">% สูงสุด</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="g in gradeRules" :key="g.label">
                            <td>
                              <v-chip x-small :color="g.color" dark>{{
                                g.label
                              }}</v-chip>
                            </td>
                            <td class="caption">{{ g.range }}</td>
                            <td class="caption font-weight-bold">
                              {{ g.pct }}%
                            </td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
        </v-card>

        <!-- ===== SUMMARY CARDS ===== -->
        <v-row class="mb-4" v-if="compensationData.length > 0">
          <v-col cols="6" sm="3">
            <v-card outlined class="text-center pa-3">
              <v-icon color="info darken-2">mdi-account-group</v-icon>
              <div
                class="text-h5 font-weight-bold info--text text--darken-2 mt-1"
              >
                {{ totalPersonnel }}
              </div>
              <div class="caption grey--text">พนักงานทั้งหมด</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card outlined class="text-center pa-3">
              <v-icon color="success">mdi-account-check</v-icon>
              <div class="text-h5 font-weight-bold success--text mt-1">
                {{ eligibleCount }}
              </div>
              <div class="caption grey--text">มีสิทธิ์รับการเลื่อน</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card outlined class="text-center pa-3">
              <v-icon color="warning darken-2">mdi-cash</v-icon>
              <div
                class="text-h6 font-weight-bold warning--text text--darken-2 mt-1"
              >
                {{ formatCurrency(totalSalary) }}
              </div>
              <div class="caption grey--text">ฐานรวม (บาท/เดือน)</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card
              outlined
              class="text-center pa-3"
              :class="remainingBudget < 0 ? 'red lighten-5' : 'green lighten-5'"
            >
              <v-icon
                :color="remainingBudget < 0 ? 'error darken-2' : 'success darken-2'"
              >
                {{
                  remainingBudget < 0 ? "mdi-alert-circle" : "mdi-check-circle"
                }}
              </v-icon>
              <div
                class="text-h6 font-weight-bold mt-1"
                :class="
                  remainingBudget < 0
                    ? 'red--text text--darken-2'
                    : 'green--text text--darken-2'
                "
              >
                {{ formatCurrency(totalCompensationBudget) }}
              </div>
              <div class="caption grey--text">วงเงินสูงสุด/ปี</div>
              <div
                class="caption font-weight-bold"
                :class="remainingBudget < 0 ? 'red--text' : 'green--text'"
              >
                {{
                  remainingBudget < 0
                    ? "เกินวงเงิน!"
                    : "คงเหลือ " + formatCurrency(remainingBudget)
                }}
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Budget Progress -->
        <v-card outlined class="mb-4 pa-3" v-if="calculationComplete">
          <v-row align="center" dense>
            <v-col cols="12" sm="2" class="caption grey--text"
              >การใช้วงเงิน:</v-col
            >
            <v-col cols="12" sm="7">
              <v-progress-linear
                :value="Math.min(budgetPercentage, 100)"
                :color="
                  budgetPercentage > 100
                    ? 'error'
                    : budgetPercentage > 90
                    ? 'warning'
                    : 'success'
                "
                height="22"
                rounded
              >
                <template v-slot:default>
                  <span class="caption white--text font-weight-bold">
                    {{ budgetPercentage.toFixed(1) }}%
                  </span>
                </template>
              </v-progress-linear>
            </v-col>
            <v-col cols="12" sm="3" class="caption text-right">
              <span
                class="font-weight-bold"
                :class="remainingBudget < 0 ? 'red--text' : 'green--text'"
              >
                {{ formatCurrency(totalCompensationDistributed) }} /
                {{ formatCurrency(totalCompensationBudget) }} บาท/ปี
              </span>
            </v-col>
          </v-row>
        </v-card>

        <!-- ===== STEP 2: จัดการ Columns + ตาราง ===== -->
        <v-card outlined class="mb-4">
          <v-card-title
            class="subtitle-1 font-weight-bold grey lighten-4 py-2 px-4"
          >
            <v-avatar color="info darken-3" size="26" class="mr-2">
              <span class="white--text caption font-weight-bold">2</span>
            </v-avatar>
            ตารางการคำนวณค่าตอบแทน
            <v-spacer />
            <!-- Filter tabs -->
            <v-btn-toggle v-model="filterMode" mandatory dense class="mr-3">
              <v-btn x-small value="all">ทั้งหมด ({{ totalPersonnel }})</v-btn>
              <v-btn x-small value="eligible" color="success"
                >มีสิทธิ์ ({{ eligibleCount }})</v-btn
              >
              <v-btn x-small value="ineligible" color="error"
                >ไม่มีสิทธิ์ ({{ ineligibleCount }})</v-btn
              >
            </v-btn-toggle>
            <!-- Column toggle -->
            <v-menu offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on }">
                <v-btn x-small outlined v-on="on" class="mr-2">
                  <v-icon x-small left>mdi-table-column</v-icon> คอลัมน์
                </v-btn>
              </template>
              <v-card class="pa-3" style="min-width:280px">
                <div class="caption font-weight-bold mb-2">
                  เลือกคอลัมน์ที่แสดง
                </div>
                <v-checkbox
                  v-for="h in allHeaders.filter(x => !x.alwaysShow)"
                  :key="h.value"
                  v-model="visibleHeaderValues"
                  :label="h.text"
                  :value="h.value"
                  dense
                  hide-details
                  class="mt-0"
                />
                <v-divider class="my-2" />
                <div class="d-flex justify-space-between">
                  <v-btn x-small text color="primary" @click="selectAllCols"
                    >ทั้งหมด</v-btn
                  >
                  <v-btn x-small text color="info" @click="selectEssentialCols"
                    >สำคัญ</v-btn
                  >
                  <v-btn x-small text color="warning" @click="resetCols"
                    >รีเซ็ต</v-btn
                  >
                </div>
              </v-card>
            </v-menu>
            <v-chip
              :color="calculationComplete ? 'success' : 'warning'"
              dark
              small
              class="mr-2"
            >
              {{ calculationComplete ? "✓ คำนวณแล้ว" : "⏳ รอคำนวณ" }}
            </v-chip>
            <v-btn
              color="primary"
              small
              @click="calculateCompensation"
              :loading="calculating"
              :disabled="!canCalculate"
            >
              <v-icon left small>mdi-calculator</v-icon> คำนวณใหม่
            </v-btn>
          </v-card-title>

          <v-data-table
            :headers="compensationHeaders"
            :items="filteredData"
            :loading="loading"
            class="elevation-0 calc-table"
            hide-default-footer
            disable-pagination
            fixed-header
            height="550px"
            dense
            :no-data-text="loading ? 'กำลังโหลด...' : 'กรุณาโหลดข้อมูลก่อน'"
          >
            <!-- ลำดับ -->
            <!-- Vuetify 2: ใช้ item.order จาก compensationData แทน index ที่ใช้ไม่ได้กับ disable-pagination -->
            <template v-slot:item.order="{ item }">
              <span class="grey--text caption">{{ item.order }}</span>
            </template>

            <!-- ชื่อ-สกุล -->
            <template v-slot:item.full_name="{ item }">
              <div class="font-weight-medium" style="white-space:nowrap">
                {{ item.full_name }}
              </div>
              <div class="caption grey--text">{{ item.name_postion }}</div>
              <div class="caption info--text text--lighten-1">
                {{ item.id_card }}
              </div>
            </template>

            <!-- สิทธิ์ -->
            <template v-slot:item.eligible="{ item }">
              <v-tooltip bottom max-width="260">
                <template v-slot:activator="{ on }">
                  <v-chip
                    x-small
                    :color="item.eligible ? 'success' : 'error'"
                    dark
                    v-on="on"
                  >
                    <v-icon x-small left>{{
                      item.eligible ? "mdi-check" : "mdi-close"
                    }}</v-icon>
                    {{ item.eligible ? "มีสิทธิ์" : "ไม่มีสิทธิ์" }}
                  </v-chip>
                </template>
                <span>{{ item.ineligible_reason || "ผ่านเกณฑ์ทุกข้อ" }}</span>
              </v-tooltip>
              <div
                v-if="!item.eligible"
                class="caption error--text mt-1"
                style="max-width:140px;white-space:normal;font-size:10px"
              >
                {{ item.ineligible_reason }}
              </div>
            </template>

            <!-- เงินเดือน -->
            <template v-slot:item.salary_s="{ item }">
              <span class="font-weight-bold">{{
                formatCurrency(item.salary_s)
              }}</span>
              <div class="caption grey--text">{{ item.name_type_group }}</div>
            </template>

            <!-- คะแนนประเมิน -->
            <template v-slot:item.performance_score="{ item }">
              <div v-if="item.performance_score > 0">
                <v-chip
                  x-small
                  :color="getGradeColor(item.performance_score)"
                  dark
                  class="mb-1"
                >
                  {{ item.performance_score }}
                </v-chip>
                <div
                  class="caption"
                  :class="
                    getGradeColor(item.performance_score).replace(
                      ' darken-1',
                      ''
                    ) + '--text'
                  "
                >
                  {{ getGradeLabel(item.performance_score) }}
                </div>
              </div>
              <v-chip v-else x-small color="grey" dark>ไม่มีข้อมูล</v-chip>
            </template>

            <!-- ข้อมูลการลา -->
            <template v-slot:item.leave_info="{ item }">
              <div class="caption" style="line-height:1.6">
                <span
                  :class="
                    item.total_counted_leave > 45
                      ? 'red--text font-weight-bold'
                      : 'grey--text'
                  "
                >
                  ลานับ {{ item.total_counted_leave }}/45วัน </span
                ><br />
                <span
                  :class="
                    item.late_count > 18
                      ? 'red--text font-weight-bold'
                      : 'grey--text'
                  "
                >
                  สาย {{ item.late_count }}/18ครั้ง
                </span>
                <span class="mx-1 grey--text">|</span>
                <span
                  :class="
                    item.absent_count > 0
                      ? 'red--text font-weight-bold'
                      : 'grey--text'
                  "
                >
                  ขาด {{ item.absent_count }}ครั้ง
                </span>
              </div>
            </template>

            <!-- สูงสุด 6% -->
            <template v-slot:item.max_increase_6_percent="{ item }">
              <span class="primary--text text--darken-1 font-weight-bold">
                {{
                  item.eligible
                    ? formatCurrency(item.max_increase_6_percent)
                    : "-"
                }}
              </span>
            </template>

            <!-- ร้อยละรายบุคคล (ก่อนบริหาร) -->
            <template v-slot:item.individual_percentage_pre_manage="{ item }">
              <span
                :class="
                  getPercentageColor(item.individual_percentage_pre_manage)
                "
              >
                {{
                  item.eligible
                    ? formatPct3(item.individual_percentage_pre_manage) + "%"
                    : "-"
                }}
              </span>
            </template>

            <!-- เลื่อนขั้นต้น -->
            <template v-slot:item.initial_increase_pre_manage="{ item }">
              <span class="info--text text--darken-1 font-weight-bold">
                {{
                  item.eligible
                    ? formatCurrency2(item.initial_increase_pre_manage)
                    : "-"
                }}
              </span>
            </template>

            <!-- ปัดเศษต้น -->
            <template v-slot:item.initial_rounded_pre_manage="{ item }">
              <span class="primary--text font-weight-bold">
                {{
                  item.eligible
                    ? formatCurrency(item.initial_rounded_pre_manage)
                    : "-"
                }}
              </span>
            </template>

            <!-- ร้อยละบริหารวงเงิน -->
            <template v-slot:item.management_percentage="{ item }">
              <span class="info--text text--darken-2 font-weight-bold">
                {{
                  item.eligible
                    ? formatPct3(item.management_percentage) + "%"
                    : "-"
                }}
              </span>
            </template>

            <!-- ร้อยละเลื่อนจริง -->
            <template v-slot:item.actual_percentage_final="{ item }">
              <span class="success--text text--darken-1 font-weight-bold">
                {{
                  item.eligible
                    ? formatPct3(item.actual_percentage_final) + "%"
                    : "-"
                }}
              </span>
            </template>

            <!-- เงินเลื่อนจริง -->
            <template v-slot:item.actual_increase_amount="{ item }">
              <span class="primary--text font-weight-bold">
                {{
                  item.eligible
                    ? formatCurrency2(item.actual_increase_amount)
                    : "-"
                }}
              </span>
            </template>

            <!-- ปัดเศษจริง (final) -->
            <template v-slot:item.final_rounded="{ item }">
              <span class="error--text text--darken-1 font-weight-bold">
                {{ item.eligible ? formatCurrency(item.final_rounded) : "-" }}
              </span>
            </template>

            <!-- เงินเดือนใหม่ -->
            <template v-slot:item.new_salary="{ item }">
              <div class="text-right">
                <span class="info--text text--darken-2 font-weight-bold">
                  {{ formatCurrency(item.new_salary) }}
                </span>
                <div v-if="item.capped" class="caption warning--text">
                  <v-icon x-small color="warning">mdi-alert</v-icon> ถึงเพดาน
                </div>
              </div>
            </template>

            <!-- ปุ่มดู -->
            <template v-slot:item.actions="{ item }">
              <v-btn icon x-small color="info" @click="viewDetails(item)">
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>

        <!-- ===== STEP 3: สรุปผลรวม (Tabs) ===== -->
        <v-card
          outlined
          class="mb-4 summary-card"
          v-if="compensationData.length > 0"
        >
          <v-card-title
            class="white--text py-3 px-4"
            style="background:linear-gradient(135deg,#455a64,#455a64)"
          >
            <v-icon left>mdi-calculator-variant</v-icon>
            สรุปผลรวมทั้งสิ้น
          </v-card-title>
          <v-card-text class="pt-3">
            <v-tabs v-model="summaryTab" background-color="grey lighten-4">
              <v-tab><v-icon small left>mdi-database</v-icon>พื้นฐาน</v-tab>
              <v-tab><v-icon small left>mdi-calculator</v-icon>ขั้นต้น</v-tab>
              <v-tab
                ><v-icon small left>mdi-cash-multiple</v-icon
                >บริหารวงเงิน</v-tab
              >
              <v-tab
                ><v-icon small left>mdi-check-decagram</v-icon
                >ผลลัพธ์จริง</v-tab
              >
            </v-tabs>

            <v-tabs-items v-model="summaryTab" class="mt-3">
              <!-- Tab 0: พื้นฐาน -->
              <v-tab-item>
                <v-row>
                  <v-col cols="6" md="3">
                    <div class="summary-item">
                      <p class="summary-label">จำนวนพนักงาน</p>
                      <p class="summary-value info--text text--darken-2">
                        {{ totalPersonnel }}
                      </p>
                      <p class="summary-unit">คน</p>
                    </div>
                  </v-col>
                  <v-col cols="6" md="3">
                    <div class="summary-item">
                      <p class="summary-label">มีสิทธิ์รับการเลื่อน</p>
                      <p class="summary-value success--text">
                        {{ eligibleCount }}
                      </p>
                      <p class="summary-unit">
                        คน ({{ ineligibleCount }} คน ไม่มีสิทธิ์)
                      </p>
                    </div>
                  </v-col>
                  <v-col cols="6" md="3">
                    <div class="summary-item">
                      <p class="summary-label">ฐานค่าตอบแทนรวม</p>
                      <p class="summary-value success--text text--darken-2">
                        {{ formatCurrency(totalSalary) }}
                      </p>
                      <p class="summary-unit">บาท/เดือน</p>
                    </div>
                  </v-col>
                  <v-col cols="6" md="3">
                    <div class="summary-item">
                      <p class="summary-label">
                        วงเงินสูงสุด ({{ compensationPercentage }}% × 12 ด.)
                      </p>
                      <p class="summary-value warning--text text--darken-2">
                        {{ formatCurrency(totalCompensationBudget) }}
                      </p>
                      <p class="summary-unit">บาท/ปี</p>
                    </div>
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- Tab 1: ขั้นต้น -->
              <v-tab-item>
                <v-row>
                  <v-col cols="6" md="3">
                    <div class="summary-item">
                      <p class="summary-label">สูงสุด 6% รวม</p>
                      <p class="summary-value primary--text text--darken-1">
                        {{ formatCurrency(totalMaxIncrease6Percent) }}
                      </p>
                      <p class="summary-unit">บาท</p>
                    </div>
                  </v-col>
                  <v-col cols="6" md="3">
                    <div class="summary-item">
                      <p class="summary-label">เงินเลื่อนขั้นต้น (ทศ.2)</p>
                      <p class="summary-value info--text text--darken-1">
                        {{ formatCurrency2(totalInitialIncreasePreManage) }}
                      </p>
                      <p class="summary-unit">บาท</p>
                    </div>
                  </v-col>
                  <v-col cols="6" md="3">
                    <div class="summary-item">
                      <p class="summary-label">ปัดเศษขั้นต้น</p>
                      <p class="summary-value primary--text">
                        {{ formatCurrency(totalInitialRoundedPreManage) }}
                      </p>
                      <p class="summary-unit">บาท</p>
                    </div>
                  </v-col>
                  <v-col cols="6" md="3">
                    <div class="summary-item">
                      <p class="summary-label">วงเงินคงเหลือสำหรับบริหาร</p>
                      <p
                        class="summary-value"
                        :class="
                          remainingBudgetAfterRounding >= 0
                            ? 'success--text'
                            : 'red--text'
                        "
                      >
                        {{ formatCurrency(remainingBudgetAfterRounding) }}
                      </p>
                      <p class="summary-unit">บาท/ปี</p>
                    </div>
                  </v-col>
                </v-row>
                <v-divider class="my-3" />
                <v-row>
                  <v-col cols="12" md="6">
                    <v-card flat class="pa-3 info-card">
                      <p class="info-title">
                        วงเงินค่าตอบแทนรวม ({{ compensationPercentage }}% × ฐาน
                        × 12)
                      </p>
                      <p class="info-value info--text text--darken-2">
                        {{ formatCurrency(totalCompensationBudget) }} บาท/ปี
                      </p>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-card flat class="pa-3 info-card">
                      <p class="info-title">ร้อยละบริหารวงเงิน (เฉลี่ย)</p>
                      <p class="info-value info--text text--darken-2">
                        {{ managementPercentageAverage.toFixed(3) }}% ต่อคน
                      </p>
                    </v-card>
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- Tab 2: บริหารวงเงิน -->
              <v-tab-item>
                <v-row>
                  <v-col cols="6" md="3">
                    <div class="summary-item">
                      <p class="summary-label">ร้อยละบริหาร (รวม)</p>
                      <p class="summary-value info--text text--darken-2">
                        {{ formatPct3(totalManagementPercentage) }}%
                      </p>
                      <p class="summary-unit">ทศนิยม 3 หลัก</p>
                    </div>
                  </v-col>
                  <v-col cols="6" md="3">
                    <div class="summary-item">
                      <p class="summary-label">ร้อยละเลื่อนจริง (รวม)</p>
                      <p class="summary-value success--text">
                        {{ formatPct3(totalActualPercentageFinal) }}%
                      </p>
                      <p class="summary-unit">ทศนิยม 3 หลัก</p>
                    </div>
                  </v-col>
                  <v-col cols="6" md="3">
                    <div class="summary-item">
                      <p class="summary-label">เงินเลื่อนจริง (ทศ.2)</p>
                      <p class="summary-value primary--text">
                        {{ formatCurrency2(totalActualIncreaseAmount) }}
                      </p>
                      <p class="summary-unit">บาท</p>
                    </div>
                  </v-col>
                  <v-col cols="6" md="3">
                    <div class="summary-item">
                      <p class="summary-label">ปัดเศษจริง (รวม)</p>
                      <p
                        class="summary-value error--text text--darken-1 font-weight-bold"
                      >
                        {{ formatCurrency(totalFinalRounded) }}
                      </p>
                      <p class="summary-unit">บาท/เดือน</p>
                    </div>
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- Tab 3: ผลลัพธ์จริง -->
              <v-tab-item>
                <v-row>
                  <v-col cols="6" md="3">
                    <div class="summary-item">
                      <p class="summary-label">ฐานค่าตอบแทนรวม (เดิม)</p>
                      <p class="summary-value grey--text text--darken-2">
                        {{ formatCurrency(totalSalary) }}
                      </p>
                      <p class="summary-unit">บาท/เดือน</p>
                    </div>
                  </v-col>
                  <v-col cols="6" md="3">
                    <div class="summary-item">
                      <p class="summary-label">เงินเลื่อนรวม</p>
                      <p
                        class="summary-value error--text text--darken-1 font-weight-bold"
                      >
                        {{ formatCurrency(totalFinalRounded) }}
                      </p>
                      <p class="summary-unit">บาท/เดือน</p>
                    </div>
                  </v-col>
                  <v-col cols="6" md="3">
                    <div class="summary-item">
                      <p class="summary-label">ฐานรวมหลังเลื่อน</p>
                      <p
                        class="summary-value info--text text--darken-2 font-weight-bold"
                      >
                        {{ formatCurrency(totalNewSalary) }}
                      </p>
                      <p class="summary-unit">บาท/เดือน</p>
                    </div>
                  </v-col>
                  <v-col cols="6" md="3">
                    <div class="summary-item">
                      <p class="summary-label">ผลการใช้วงเงิน</p>
                      <p class="summary-value" :class="budgetStatus.class">
                        {{ budgetPercentage.toFixed(2) }}%
                      </p>
                      <p class="summary-unit">{{ budgetStatus.text }}</p>
                    </div>
                  </v-col>
                </v-row>
                <v-divider class="my-3" />
                <v-row>
                  <v-col cols="12" md="6">
                    <v-card
                      class="pa-4 text-center"
                      outlined
                      :color="
                        remainingBudget >= 0
                          ? 'success lighten-5'
                          : 'error lighten-5'
                      "
                    >
                      <p class="caption font-weight-bold mb-1">วงเงินคงเหลือ</p>
                      <p
                        class="text-h5 font-weight-bold mb-1"
                        :class="
                          remainingBudget >= 0
                            ? 'green--text text--darken-2'
                            : 'red--text text--darken-2'
                        "
                      >
                        {{ formatCurrency(Math.abs(remainingBudget)) }} บาท/ปี
                      </p>
                      <p class="caption">
                        {{
                          remainingBudget >= 0
                            ? "✓ อยู่ในควบคุม"
                            : "✗ เกินวงเงิน"
                        }}
                      </p>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-card
                      class="pa-4 text-center"
                      outlined
                      color="info lighten-5"
                    >
                      <p class="caption font-weight-bold mb-1">
                        อัตราเลื่อนเฉลี่ย
                      </p>
                      <p
                        class="text-h5 font-weight-bold info--text text--darken-2 mb-1"
                      >
                        {{
                          totalSalary > 0
                            ? ((totalFinalRounded / totalSalary) * 100).toFixed(
                                3
                              )
                            : "0.000"
                        }}%
                      </p>
                      <p class="caption">เฉลี่ยต่อฐานรวม</p>
                    </v-card>
                  </v-col>
                </v-row>
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>

        <!-- ===== ขั้นตอนคำนวณ (Expansion) ===== -->
        <v-expansion-panels class="mb-4">
          <v-expansion-panel>
            <v-expansion-panel-header>
              <span
                ><v-icon left small color="info">mdi-information-outline</v-icon
                >วิธีการคำนวณแบบละเอียด (9 ขั้นตอน)</span
              >
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-timeline align-top dense class="mt-2">
                <v-timeline-item
                  v-for="(step, i) in calcSteps"
                  :key="i"
                  small
                  color="info darken-2"
                >
                  <div class="caption font-weight-bold">
                    ขั้นที่ {{ i + 1 }}: {{ step.title }}
                  </div>
                  <code class="caption d-block mt-1 pa-1 grey lighten-4">{{
                    step.formula
                  }}</code>
                  <div
                    v-if="step.note"
                    class="caption warning--text text--darken-2 mt-1"
                  >
                    ⚠️ {{ step.note }}
                  </div>
                </v-timeline-item>
              </v-timeline>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-header>
              <span
                ><v-icon left small color="warning">mdi-check-circle</v-icon
                >ตรวจสอบความถูกต้อง</span
              >
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-simple-table dense class="mt-2">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>เกณฑ์</th>
                      <th>ค่าปัจจุบัน</th>
                      <th>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="check in validationChecks" :key="check.label">
                      <td class="caption">{{ check.label }}</td>
                      <td class="caption">{{ check.value }}</td>
                      <td>
                        <v-chip
                          x-small
                          :color="check.pass ? 'success' : 'error'"
                          dark
                        >
                          {{ check.pass ? "✓ ผ่าน" : "✗ ไม่ผ่าน" }}
                        </v-chip>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- ===== STEP 4: ปุ่มดำเนินการ ===== -->
        <v-card outlined class="mb-4">
          <v-card-title
            class="subtitle-1 font-weight-bold grey lighten-4 py-2 px-4"
          >
            <v-avatar color="info darken-3" size="26" class="mr-2">
              <span class="white--text caption font-weight-bold">3</span>
            </v-avatar>
            บันทึกผลการเลื่อนค่าตอบแทน
          </v-card-title>
          <v-card-text>
            <v-row align="center">
              <v-col cols="12" md="7">
                <v-alert type="info" dense outlined class="caption mb-0">
                  <strong>สรุปการดำเนินการ:</strong>
                  พนักงานที่ได้รับการเลื่อน
                  <strong>{{ eligibleCount }} คน</strong> วงเงิน
                  <strong
                    >{{ formatCurrency(totalFinalRounded) }} บาท/เดือน</strong
                  >
                  ({{ formatCurrency(totalFinalRounded * 12) }} บาท/ปี) · มีผล
                  <strong>1 ตุลาคม {{ selectedYear }}</strong>
                </v-alert>
              </v-col>
              <v-col
                cols="12"
                md="5"
                class="d-flex justify-end flex-wrap"
                style="gap:8px"
              >
                <v-btn
                  color="primary"
                  :loading="calculating"
                  :disabled="loading || !canCalculate"
                  @click="calculateCompensation"
                >
                  <v-icon left>mdi-calculator</v-icon> คำนวณ
                </v-btn>
                <v-btn
                  color="success"
                  :disabled="!canApprove"
                  @click="approveCompensation"
                >
                  <v-icon left>mdi-check-circle</v-icon> อนุมัติ
                </v-btn>
                <v-btn
                  color="info"
                  :disabled="
                    !calculationComplete || compensationData.length === 0
                  "
                  @click="exportCSV"
                >
                  <v-icon left>mdi-file-export</v-icon> CSV
                </v-btn>
                <v-btn color="warning" :disabled="loading" @click="resetAll">
                  <v-icon left>mdi-refresh</v-icon> เริ่มใหม่
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </base-material-card>

      <!-- ===== DIALOG รายละเอียด ===== -->
      <v-dialog v-model="detailDialog" max-width="860" scrollable>
        <v-card v-if="selectedEmployee">
          <v-card-title
            class="white--text py-3 px-4 d-flex justify-space-between"
            style="background:linear-gradient(135deg,#455a64,#455a64)"
          >
            <span
              ><v-icon left>mdi-account-details</v-icon
              >รายละเอียดการคำนวณค่าตอบแทน</span
            >
            <v-btn icon color="white" @click="detailDialog = false"
              ><v-icon>mdi-close</v-icon></v-btn
            >
          </v-card-title>
          <v-card-text class="pt-4">
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  label="ชื่อ-สกุล"
                  :value="selectedEmployee.full_name"
                  readonly
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  label="ตำแหน่ง"
                  :value="selectedEmployee.name_postion"
                  readonly
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  label="กลุ่มงาน"
                  :value="selectedEmployee.name_type_group"
                  readonly
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  label="เงินเดือนปัจจุบัน (บาท)"
                  :value="formatCurrency(selectedEmployee.salary_s)"
                  readonly
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  :label="`คะแนนผลการปฏิบัติงาน (รอบ ${assessmentRound})`"
                  :value="
                    selectedEmployee.performance_score +
                      ' (' +
                      getGradeLabel(selectedEmployee.performance_score) +
                      ')'
                  "
                  readonly
                  outlined
                  dense
                />
              </v-col>
            </v-row>
            <v-divider class="my-2" />
            <div class="subtitle-2 mb-2 info--text text--darken-2">
              ข้อมูลการลา/สาย/ขาด
            </div>
            <v-row dense>
              <v-col cols="6" md="2"
                ><v-text-field
                  label="ลาป่วย (วัน)"
                  :value="selectedEmployee.sick_leave_days"
                  readonly
                  outlined
                  dense
              /></v-col>
              <v-col cols="6" md="2"
                ><v-text-field
                  label="ลาคลอด (วัน)"
                  :value="selectedEmployee.maternity_leave_days"
                  readonly
                  outlined
                  dense
              /></v-col>
              <v-col cols="6" md="2"
                ><v-text-field
                  label="ลากิจ (วัน)"
                  :value="selectedEmployee.personal_leave_days"
                  readonly
                  outlined
                  dense
              /></v-col>
              <v-col cols="6" md="2"
                ><v-text-field
                  label="ลากิจ (ครั้ง)"
                  :value="selectedEmployee.personal_leave_times"
                  readonly
                  outlined
                  dense
              /></v-col>
              <v-col cols="6" md="2"
                ><v-text-field
                  label="มาสาย (ครั้ง)"
                  :value="selectedEmployee.late_count"
                  readonly
                  outlined
                  dense
              /></v-col>
              <v-col cols="6" md="2"
                ><v-text-field
                  label="ขาดราชการ (ครั้ง)"
                  :value="selectedEmployee.absent_count"
                  readonly
                  outlined
                  dense
              /></v-col>
            </v-row>
            <v-chip
              class="mb-3"
              small
              :color="selectedEmployee.eligible ? 'success' : 'error'"
              dark
            >
              {{
                selectedEmployee.eligible
                  ? "✓ มีสิทธิ์รับการเลื่อน"
                  : "✗ ไม่มีสิทธิ์: " + selectedEmployee.ineligible_reason
              }}
            </v-chip>

            <v-divider class="my-2" />
            <div class="subtitle-2 mb-2 info--text text--darken-2">
              ผลการคำนวณ (ยังไม่บริหารวงเงิน)
            </div>
            <v-row dense>
              <v-col cols="6" md="3"
                ><v-text-field
                  label="สูงสุด 6%"
                  :value="
                    formatCurrency(selectedEmployee.max_increase_6_percent) +
                      ' บาท'
                  "
                  readonly
                  outlined
                  dense
              /></v-col>
              <v-col cols="6" md="3"
                ><v-text-field
                  label="ร้อยละรายบุคคล"
                  :value="
                    formatPct3(
                      selectedEmployee.individual_percentage_pre_manage
                    ) + '%'
                  "
                  readonly
                  outlined
                  dense
              /></v-col>
              <v-col cols="6" md="3"
                ><v-text-field
                  label="เงินเลื่อนขั้นต้น"
                  :value="
                    formatCurrency2(
                      selectedEmployee.initial_increase_pre_manage
                    ) + ' บาท'
                  "
                  readonly
                  outlined
                  dense
              /></v-col>
              <v-col cols="6" md="3"
                ><v-text-field
                  label="ปัดเศษขั้นต้น"
                  :value="
                    formatCurrency(
                      selectedEmployee.initial_rounded_pre_manage
                    ) + ' บาท'
                  "
                  readonly
                  outlined
                  dense
              /></v-col>
            </v-row>
            <v-divider class="my-2" />
            <div class="subtitle-2 mb-2 info--text text--darken-2">
              ผลหลังบริหารวงเงิน
            </div>
            <v-row dense>
              <v-col cols="6" md="3"
                ><v-text-field
                  label="ร้อยละบริหารวงเงิน"
                  :value="
                    formatPct3(selectedEmployee.management_percentage) + '%'
                  "
                  readonly
                  outlined
                  dense
              /></v-col>
              <v-col cols="6" md="3"
                ><v-text-field
                  label="ร้อยละเลื่อนจริง"
                  :value="
                    formatPct3(selectedEmployee.actual_percentage_final) + '%'
                  "
                  readonly
                  outlined
                  dense
              /></v-col>
              <v-col cols="6" md="3"
                ><v-text-field
                  label="เงินเลื่อนจริง"
                  :value="
                    formatCurrency2(selectedEmployee.actual_increase_amount) +
                      ' บาท'
                  "
                  readonly
                  outlined
                  dense
              /></v-col>
              <v-col cols="6" md="3"
                ><v-text-field
                  label="ปัดเศษจริง (ข้อ 1(6))"
                  :value="
                    formatCurrency(selectedEmployee.final_rounded) + ' บาท'
                  "
                  readonly
                  outlined
                  dense
              /></v-col>
            </v-row>
            <v-divider class="my-2" />
            <div class="subtitle-2 mb-2 success--text">ผลลัพธ์สุดท้าย</div>
            <v-row dense>
              <v-col cols="12" md="4"
                ><v-text-field
                  label="ค่าตอบแทนเดิม"
                  :value="formatCurrency(selectedEmployee.salary_s) + ' บาท'"
                  readonly
                  outlined
                  dense
              /></v-col>
              <v-col cols="12" md="4"
                ><v-text-field
                  label="เงินที่ได้รับเพิ่ม"
                  :value="
                    formatCurrency(selectedEmployee.final_rounded) + ' บาท'
                  "
                  readonly
                  outlined
                  dense
              /></v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  label="ค่าตอบแทนใหม่"
                  :value="formatCurrency(selectedEmployee.new_salary) + ' บาท'"
                  readonly
                  outlined
                  dense
                  background-color="info lighten-5"
                  class="font-weight-bold"
                />
                <div
                  v-if="selectedEmployee.capped"
                  class="caption warning--text"
                >
                  <v-icon x-small color="warning">mdi-alert</v-icon>
                  ถึงเพดานขั้นสูงกลุ่มงาน ({{
                    formatCurrency(
                      getGroupMax(selectedEmployee.name_type_group)
                    )
                  }}
                  บาท)
                </div>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text color="info darken-1" @click="detailDialog = false"
              >ปิด</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ===== DIALOG ยืนยันอนุมัติ ===== -->
      <v-dialog v-model="approvalDialog" max-width="520" persistent>
        <v-card>
          <v-card-title
            class="white--text py-3 px-4"
            style="background:linear-gradient(135deg,#2e7d32,#2e7d32)"
          >
            <v-icon left>mdi-check-circle</v-icon>
            ยืนยันการอนุมัติเลื่อนค่าตอบแทน
          </v-card-title>
          <v-card-text class="pt-4">
            <v-simple-table dense>
              <tbody>
                <tr>
                  <td class="caption grey--text">ปีงบประมาณ</td>
                  <td class="font-weight-bold">พ.ศ. {{ selectedYear }}</td>
                </tr>
                <tr>
                  <td class="caption grey--text">มีผลวันที่</td>
                  <td class="font-weight-bold">1 ตุลาคม {{ selectedYear }}</td>
                </tr>
                <tr>
                  <td class="caption grey--text">จำนวนพนักงานที่ได้รับ</td>
                  <td class="font-weight-bold success--text">
                    {{ eligibleCount }} คน
                  </td>
                </tr>
                <tr>
                  <td class="caption grey--text">วงเงินที่จัดสรร (ต่อเดือน)</td>
                  <td class="font-weight-bold primary--text">
                    {{ formatCurrency(totalFinalRounded) }} บาท
                  </td>
                </tr>
                <tr>
                  <td class="caption grey--text">วงเงินที่จัดสรร (ต่อปี)</td>
                  <td class="font-weight-bold primary--text">
                    {{ formatCurrency(totalFinalRounded * 12) }} บาท
                  </td>
                </tr>
                <tr>
                  <td class="caption grey--text">% ของวงเงินที่ใช้</td>
                  <td>
                    <v-chip small :color="budgetStatus.color" dark
                      >{{ budgetPercentage.toFixed(2) }}%</v-chip
                    >
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
            <v-alert type="warning" dense outlined class="caption mt-3">
              <strong>คำเตือน:</strong> การดำเนินการนี้ไม่สามารถยกเลิกได้
              กรุณาตรวจสอบข้อมูลให้ถูกต้องก่อนยืนยัน
            </v-alert>
          </v-card-text>
          <v-card-actions class="pa-4">
            <v-spacer />
            <v-btn outlined @click="approvalDialog = false" :disabled="saving"
              >ยกเลิก</v-btn
            >
            <v-btn
              color="success"
              dark
              :loading="saving"
              @click="confirmApproval"
            >
              <v-icon left>mdi-check</v-icon> ยืนยันอนุมัติ
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ===== SNACKBAR ===== -->
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        top
        right
        timeout="5000"
        multi-line
      >
        <v-icon left>{{ snackbar.icon }}</v-icon>
        {{ snackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">ปิด</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import Govem_bar from "../../../components/college/govem_bar.vue";

// ============================================================
// ขั้นสูงกลุ่มงาน ตามประกาศ พ.ศ. 2568 (แนบท้าย)
// ============================================================
const GROUP_MAX_SALARY = {
  บริการ: 20210,
  เทคนิคทั่วไป: 24930,
  เทคนิคพิเศษ: 59790,
  บริหารทั่วไป: 34700,
  วิชาชีพเฉพาะ: 44550,
  เชี่ยวชาญเฉพาะ: 68350
};
const GROUP_KEY_MAP = {
  บริการ: "บริการ",
  เทคนิค: "เทคนิคทั่วไป",
  เทคนิคทั่วไป: "เทคนิคทั่วไป",
  เทคนิคพิเศษ: "เทคนิคพิเศษ",
  บริหารทั่วไป: "บริหารทั่วไป",
  วิชาชีพเฉพาะ: "วิชาชีพเฉพาะ",
  เชี่ยวชาญเฉพาะ: "เชี่ยวชาญเฉพาะ"
};

// ปัดเศษ: เศษไม่ถึง 10 บาท → ปรับขึ้น 10 บาท (ข้อ 1(6))
function roundUpTen(val) {
  const r = val % 10;
  return r === 0 ? val : val - r + 10;
}

function gradeColor(score) {
  if (!score || score <= 0) return "grey";
  if (score >= 90) return "primary";
  if (score >= 80) return "blue darken-1";
  if (score >= 70) return "green darken-1";
  if (score >= 60) return "orange darken-1";
  return "red darken-1";
}
function gradeLabel(score) {
  if (!score || score <= 0) return "ไม่มีข้อมูล";
  if (score >= 90) return "ดีเด่น";
  if (score >= 80) return "ดีมาก";
  if (score >= 70) return "ดี";
  if (score >= 60) return "พอใช้";
  return "ต้องปรับปรุง";
}

export default {
  name: "CalculateCompensation",
  components: { Govem_bar },

  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      calculating: false,
      saving: false,

      // การตั้งค่า
      selectedYear: new Date().getFullYear() + 543,
      compensationPercentage: 4.0, // วงเงินรวม ≤ 4%
      maxAllowedPercentage: 6.0, // สูงสุดรายบุคคล ≤ 6%
      assessmentRound: 2, // รอบประเมินที่ใช้คำนวณ

      // สถานะ
      calculationComplete: false,
      detailDialog: false,
      approvalDialog: false,
      selectedEmployee: null,
      summaryTab: 0,
      filterMode: "all",

      // ข้อมูล
      user: {},
      personnelList: [], // raw จาก API
      attendanceList: [], // raw จาก API
      compensationData: [], // processed

      // ตัวเลข summary
      totalPersonnel: 0,
      totalSalary: 0,
      totalCompensationBudget: 0,
      totalCompensationDistributed: 0,
      remainingBudget: 0,

      years: [],

      // Column management
      visibleHeaderValues: [],
      allHeaders: [
        {
          text: "#",
          value: "order",
          width: "50px",
          align: "center",
          alwaysShow: true,
          essential: true
        },
        {
          text: "ชื่อ-สกุล",
          value: "full_name",
          width: "200px",
          alwaysShow: true,
          essential: true
        },
        {
          text: "สิทธิ์",
          value: "eligible",
          width: "140px",
          align: "center",
          alwaysShow: true,
          essential: true
        },
        {
          text: "ค่าตอบแทน (บาท/ด.)",
          value: "salary_s",
          width: "130px",
          align: "right",
          alwaysShow: false,
          essential: true
        },
        {
          text: "คะแนนประเมิน",
          value: "performance_score",
          width: "120px",
          align: "center",
          alwaysShow: false,
          essential: true
        },
        {
          text: "การลา/สาย/ขาด",
          value: "leave_info",
          width: "180px",
          alwaysShow: false,
          essential: true
        },
        {
          text: "สูงสุด 6% (บาท)",
          value: "max_increase_6_percent",
          width: "120px",
          align: "right",
          alwaysShow: false,
          essential: false
        },
        {
          text: "% รายบุคคล (ก่อนบริหาร)",
          value: "individual_percentage_pre_manage",
          width: "140px",
          align: "center",
          alwaysShow: false,
          essential: true
        },
        {
          text: "เลื่อนขั้นต้น (บาท)",
          value: "initial_increase_pre_manage",
          width: "130px",
          align: "right",
          alwaysShow: false,
          essential: true
        },
        {
          text: "ปัดเศษต้น (บาท)",
          value: "initial_rounded_pre_manage",
          width: "120px",
          align: "right",
          alwaysShow: false,
          essential: false
        },
        {
          text: "% บริหารวงเงิน",
          value: "management_percentage",
          width: "130px",
          align: "center",
          alwaysShow: false,
          essential: false
        },
        {
          text: "% เลื่อนจริง",
          value: "actual_percentage_final",
          width: "120px",
          align: "center",
          alwaysShow: false,
          essential: true
        },
        {
          text: "เลื่อนจริง (บาท)",
          value: "actual_increase_amount",
          width: "130px",
          align: "right",
          alwaysShow: false,
          essential: true
        },
        {
          text: "ปัดเศษจริง (บาท)",
          value: "final_rounded",
          width: "120px",
          align: "right",
          alwaysShow: false,
          essential: true
        },
        {
          text: "ค่าตอบแทนใหม่ (บาท)",
          value: "new_salary",
          width: "140px",
          align: "right",
          alwaysShow: false,
          essential: true
        },
        {
          text: "ดู",
          value: "actions",
          width: "60px",
          align: "center",
          alwaysShow: true,
          essential: true,
          sortable: false
        }
      ],

      snackbar: {
        show: false,
        color: "success",
        icon: "mdi-check",
        text: "",
        timeout: 5000
      },

      // ข้อมูลคงที่
      gradeRules: [
        { label: "ดีเด่น", range: "90-100", pct: 6, color: "primary" },
        { label: "ดีมาก", range: "80-89", pct: 5, color: "blue darken-1" },
        { label: "ดี", range: "70-79", pct: 4, color: "green darken-1" },
        { label: "พอใช้", range: "60-69", pct: 2, color: "orange darken-1" },
        { label: "ต้องปรับ", range: "< 60", pct: 0, color: "red darken-1" }
      ],
      calcSteps: [
        {
          title: "คำนวณสูงสุด 6% รายบุคคล",
          formula: "max_increase_6% = เงินเดือน × 0.06"
        },
        {
          title: "คำนวณร้อยละรายบุคคล (ยังไม่บริหาร)",
          formula:
            "individual_pct = (maxAllowed% × คะแนนบุคคล) / คะแนนรวมทุกคนที่มีสิทธิ์",
          note: "ทศนิยม 3 หลัก"
        },
        {
          title: "คำนวณเงินเลื่อนขั้นต้น",
          formula: "initial_increase = เงินเดือน × individual_pct / 100",
          note: "ทศนิยม 2 หลัก"
        },
        {
          title: "ปัดเศษขึ้นเป็นหลักสิบ (ขั้นต้น)",
          formula: "initial_rounded = ROUNDUP(initial_increase / 10) × 10",
          note: "ตาม ข้อ 1(6): เศษไม่ถึง 10 บาท → ขึ้น 10 บาท"
        },
        {
          title: "คำนวณวงเงินที่เหลือสำหรับบริหาร",
          formula: "remaining_budget = วงเงินรวม - Σ(initial_rounded)"
        },
        {
          title: "คำนวณร้อยละบริหารวงเงิน (เฉลี่ย)",
          formula:
            "management_pct = (remaining_budget / เงินเดือนรวมที่มีสิทธิ์) × 100 / 12",
          note: "แบ่งเท่ากันทุกราย"
        },
        {
          title: "คำนวณร้อยละเลื่อนจริง",
          formula: "actual_pct = individual_pct + management_pct",
          note: "ต้องไม่เกิน maxAllowed% ต่อคน"
        },
        {
          title: "คำนวณเงินเลื่อนจริงและปัดเศษ",
          formula:
            "actual_increase = เงินเดือน × actual_pct / 100 → ปัดเศษขึ้น 10 บาท",
          note: "ตรวจเพดานขั้นสูงกลุ่มงาน ข้อ 1(4)"
        },
        {
          title: "คำนวณค่าตอบแทนใหม่",
          formula: "new_salary = เงินเดือนเดิม + final_rounded"
        }
      ]
    };
  },

  computed: {
    compensationHeaders() {
      return this.allHeaders.filter(
        h => h.alwaysShow || this.visibleHeaderValues.includes(h.value)
      );
    },

    filteredData() {
      let list;
      if (this.filterMode === "eligible")
        list = this.compensationData.filter(e => e.eligible);
      else if (this.filterMode === "ineligible")
        list = this.compensationData.filter(e => !e.eligible);
      else list = this.compensationData;
      // Re-assign order ตาม index จริงของ filtered list → ป้องกัน NaN ใน Vuetify 2
      return list.map((e, i) => ({ ...e, order: i + 1 }));
    },

    eligibleCount() {
      return this.compensationData.filter(e => e.eligible).length;
    },
    ineligibleCount() {
      return this.compensationData.filter(e => !e.eligible).length;
    },

    canCalculate() {
      return this.compensationData.length > 0 && this.eligibleCount > 0;
    },

    canApprove() {
      return (
        this.calculationComplete &&
        this.totalCompensationDistributed > 0 &&
        this.totalCompensationDistributed <= this.totalCompensationBudget
      );
    },

    budgetStatus() {
      if (!this.calculationComplete)
        return { text: "ยังไม่คำนวณ", color: "grey", class: "grey--text" };
      if (this.budgetPercentage > 100)
        return { text: "เกินวงเงิน", color: "error", class: "red--text" };
      if (this.budgetPercentage >= 95)
        return { text: "ใกล้เต็ม", color: "warning", class: "orange--text" };
      return { text: "อยู่ในควบคุม", color: "success", class: "success--text" };
    },

    budgetPercentage() {
      if (!this.totalCompensationBudget) return 0;
      return (
        (this.totalCompensationDistributed / this.totalCompensationBudget) * 100
      );
    },

    // Σ ของคอลัมน์ต่างๆ (เฉพาะที่มีสิทธิ์)
    totalMaxIncrease6Percent() {
      return this._sum("max_increase_6_percent");
    },
    totalIndividualPercentagePreManage() {
      return this._sum("individual_percentage_pre_manage");
    },
    totalInitialIncreasePreManage() {
      return this._sum("initial_increase_pre_manage");
    },
    totalInitialRoundedPreManage() {
      return this._sum("initial_rounded_pre_manage");
    },
    totalManagementPercentage() {
      return this._sum("management_percentage");
    },
    totalActualPercentageFinal() {
      return this._sum("actual_percentage_final");
    },
    totalActualIncreaseAmount() {
      return this._sum("actual_increase_amount");
    },
    totalFinalRounded() {
      return this._sum("final_rounded");
    },
    totalNewSalary() {
      return this.compensationData.reduce((s, e) => s + (e.new_salary || 0), 0);
    },

    remainingBudgetAfterRounding() {
      return (
        this.totalCompensationBudget - this.totalInitialRoundedPreManage * 12
      );
    },
    managementPercentageAverage() {
      const eligibleSalary = this.compensationData
        .filter(e => e.eligible)
        .reduce((s, e) => s + e.salary_s, 0);
      if (!eligibleSalary) return 0;
      return ((this.remainingBudgetAfterRounding / eligibleSalary) * 100) / 12;
    },

    validationChecks() {
      return [
        {
          label: "ร้อยละวงเงินรวม ≤ 4%",
          value: this.compensationPercentage + "%",
          pass: this.compensationPercentage <= 4
        },
        {
          label: "สูงสุดรายบุคคล ≤ 6%",
          value: this.maxAllowedPercentage + "%",
          pass: this.maxAllowedPercentage <= 6
        },
        {
          label: "วงเงินที่ใช้ไม่เกินวงเงินรวม",
          value:
            this.formatCurrency(this.totalCompensationDistributed) +
            " / " +
            this.formatCurrency(this.totalCompensationBudget) +
            " บาท",
          pass:
            this.totalCompensationDistributed <= this.totalCompensationBudget
        },
        {
          label: "มีพนักงานที่มีสิทธิ์",
          value: this.eligibleCount + " คน",
          pass: this.eligibleCount > 0
        },
        {
          label: "สถานะการคำนวณ",
          value: this.calculationComplete ? "สมบูรณ์" : "รอคำนวณ",
          pass: this.calculationComplete
        }
      ];
    }
  },

  async mounted() {
    this.generateYears();
    this.resetCols();
    try {
      const userSession = JSON.parse(localStorage.getItem("user")) || {};
      const res = await this.$http.post("user.php", {
        ApiKey: this.ApiKey,
        user_ID: userSession.user_ID
      });
      this.user = res.data;
    } catch (e) {
      this.showSnack("ไม่สามารถโหลดข้อมูล user ได้", "warning", "mdi-alert");
    }
  },

  methods: {
    // ============================================================
    // Column management
    // ============================================================
    selectAllCols() {
      this.visibleHeaderValues = this.allHeaders
        .filter(h => !h.alwaysShow)
        .map(h => h.value);
    },
    selectEssentialCols() {
      this.visibleHeaderValues = this.allHeaders
        .filter(h => !h.alwaysShow && h.essential)
        .map(h => h.value);
    },
    resetCols() {
      this.selectEssentialCols();
    },

    // ============================================================
    // ยูทิลิตี้
    // ============================================================
    generateYears() {
      const y = new Date().getFullYear() + 543;
      this.years = [y - 2, y - 1, y, y + 1];
    },
    onYearChange() {
      this.compensationData = [];
      this.calculationComplete = false;
    },
    _sum(field) {
      return this.compensationData
        .filter(e => e.eligible)
        .reduce((s, e) => s + (parseFloat(e[field]) || 0), 0);
    },
    getGroupMax(groupName) {
      return (
        GROUP_MAX_SALARY[GROUP_KEY_MAP[groupName] || "บริหารทั่วไป"] || 34700
      );
    },
    getGradeColor(score) {
      return gradeColor(score);
    },
    getGradeLabel(score) {
      return gradeLabel(score);
    },
    getPercentageColor(pct) {
      if (pct >= 5) return "success--text";
      if (pct >= 3) return "info--text";
      if (pct > 0) return "warning--text";
      return "error--text";
    },

    formatCurrency(v) {
      if (v === null || v === undefined || isNaN(v)) return "-";
      return Number(v).toLocaleString("th-TH", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    },
    formatCurrency2(v) {
      if (v === null || v === undefined || isNaN(v)) return "-";
      return Number(v).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    formatPct3(v) {
      if (v === null || v === undefined || isNaN(v)) return "0.000";
      return Number(v).toFixed(3);
    },
    showSnack(text, color = "success", icon = "mdi-check") {
      this.snackbar = { show: true, text, color, icon, timeout: 5000 };
    },

    // ============================================================
    // โหลดข้อมูลทั้งหมด
    // ============================================================
    async loadAllData() {
      this.loading = true;
      this.compensationData = [];
      this.calculationComplete = false;
      try {
        // 1) พนักงาน (รวม performance_score รอบที่เลือก จาก subquery ใน PHP)
        const empRes = await this.$http.post("personnel_temporary_govem.php", {
          ApiKey: this.ApiKey,
          user_code: this.user.user_code,
          user_status: "gov_em"
        });
        this.personnelList = Array.isArray(empRes.data) ? empRes.data : [];

        // 2) การลา/สาย/ขาด ของปีที่เลือก
        const attRes = await this.$http.post("gov_em_attendance.php", {
          ApiKey: this.ApiKey,
          action: "get",
          college_code: this.user.user_code,
          attendance_year: this.selectedYear,
          user_status: "gov_em"
        });
        this.attendanceList = Array.isArray(attRes.data) ? attRes.data : [];

        // 3) ผลประเมิน (ดึงเฉพาะรอบที่เลือก ถ้า PHP ยังไม่รองรับ fallback จาก personnel)
        let assessMap = {};
        try {
          const assRes = await this.$http.post("gov_em_assessment.php", {
            ApiKey: this.ApiKey,
            action: "get",
            college_code: this.user.user_code,
            assessment_year: this.selectedYear,
            assessment_round: this.assessmentRound
          });
          const assList = Array.isArray(assRes.data)
            ? assRes.data
            : assRes.data.data || [];
          assList.forEach(a => {
            assessMap[String(a.id_card)] = parseFloat(a.total_score || 0);
          });
        } catch (e) {
          // fallback: ใช้ performance_score จาก personnel (รอบ 2 ที่ subquery ดึงมา)
        }

        // 4) สร้าง attendance map
        const attMap = {};
        this.attendanceList.forEach(a => {
          attMap[String(a.id_card)] = a;
        });

        // 5) รวมข้อมูลและตรวจสิทธิ์
        this.compensationData = this.personnelList.map((emp, idx) => {
          const att = attMap[String(emp.id_card)] || {};
          const score =
            assessMap[String(emp.id_card)] ||
            parseFloat(emp.performance_score || 0);
          return this.buildRecord(emp, att, score, idx + 1);
        });

        // 6) คำนวณ totalSalary
        this.calcTotalSalary();

        if (this.compensationData.length === 0) {
          this.showSnack(
            "ไม่พบข้อมูลพนักงาน กรุณาตรวจสอบปีและหน่วยงาน",
            "warning",
            "mdi-alert"
          );
        } else {
          this.showSnack(
            `โหลดข้อมูลสำเร็จ: ${this.compensationData.length} คน (มีสิทธิ์ ${this.eligibleCount} คน)`,
            "success",
            "mdi-check"
          );
        }
      } catch (err) {
        this.showSnack(
          "โหลดข้อมูลไม่สำเร็จ: " + err.message,
          "error",
          "mdi-alert"
        );
      } finally {
        this.loading = false;
      }
    },

    // ============================================================
    // สร้าง record พร้อมตรวจสิทธิ์ตามประกาศ
    // ============================================================
    buildRecord(emp, att, score, order) {
      const salary = parseFloat(emp.salary_s || 0);
      const sickDays = parseFloat(att.sick_leave_days || 0);
      const maternityDays = parseFloat(att.maternity_leave_days || 0);
      const personalDays = parseFloat(att.personal_leave_days || 0);
      const militaryDays = parseFloat(att.military_leave_days || 0);
      const personalTimes = parseInt(att.personal_leave_times || 0);
      const lateCount = parseInt(att.late_count || 0);
      const absentCount = parseInt(att.absent_count || 0);

      // วันลาที่นับตามข้อ 1(8):
      // - ลาคลอดบุตร ≤ 90 วัน ไม่นับ
      // - ลาราชการทหาร ไม่นับ
      // - ลาพักผ่อน ไม่นับ
      // - ลาป่วย + ลากิจ นับรวม ≤ 45 วัน
      const excessMaternity = Math.max(0, maternityDays - 90);
      const totalCountedLeave = sickDays + personalDays + excessMaternity;

      // ตรวจสิทธิ์
      let eligible = true;
      let ineligible_reason = "";

      if (!score || score <= 0) {
        eligible = false;
        ineligible_reason =
          "ยังไม่มีผลการประเมิน (รอบ " + this.assessmentRound + ")";
      } else if (score < 60) {
        eligible = false;
        ineligible_reason = `คะแนน ${score} ต่ำกว่าระดับดี (< 60) ข้อ 1(2)`;
      } else if (absentCount > 0) {
        eligible = false;
        ineligible_reason = `ขาดราชการ ${absentCount} ครั้ง ข้อ 1(7)`;
      } else if (totalCountedLeave > 45) {
        eligible = false;
        ineligible_reason = `วันลา ${totalCountedLeave} วัน เกิน 45 วัน ข้อ 1(8)`;
      } else if (personalTimes > 14) {
        eligible = false;
        ineligible_reason = `ลากิจ ${personalTimes} ครั้ง เกิน 14 ครั้ง ข้อ 1(9)`;
      } else if (lateCount > 18) {
        eligible = false;
        ineligible_reason = `มาสาย ${lateCount} ครั้ง เกิน 18 ครั้ง ข้อ 1(9)`;
      }

      return {
        order,
        id_card: String(emp.id_card || ""),
        full_name: `${emp.title_s || ""}${emp.frist_name ||
          ""} ${emp.last_name || ""}`.trim(),
        name_postion: emp.name_postion || "",
        name_type_group: emp.name_type_group || "บริหารทั่วไป",
        college_code: emp.college_code || this.user.user_code,
        salary_s: salary,
        performance_score: score,

        // ข้อมูลการลา
        sick_leave_days: sickDays,
        maternity_leave_days: maternityDays,
        personal_leave_days: personalDays,
        personal_leave_times: personalTimes,
        military_leave_days: militaryDays,
        late_count: lateCount,
        absent_count: absentCount,
        total_counted_leave: totalCountedLeave,

        // สิทธิ์
        eligible,
        ineligible_reason,

        // ผลคำนวณ (เริ่มต้นเป็น 0)
        max_increase_6_percent: 0,
        individual_percentage_pre_manage: 0,
        initial_increase_pre_manage: 0,
        initial_rounded_pre_manage: 0,
        management_percentage: 0,
        actual_percentage_final: 0,
        actual_increase_amount: 0,
        final_rounded: 0,
        new_salary: salary,
        capped: false
      };
    },

    // ============================================================
    // คำนวณ totalSalary และ totalCompensationBudget
    // ============================================================
    calcTotalSalary() {
      this.totalPersonnel = this.compensationData.length;
      this.totalSalary = this.compensationData.reduce(
        (s, e) => s + e.salary_s,
        0
      );
      // วงเงิน = % × ฐาน × 12 เดือน (ข้อ 1(3))
      this.totalCompensationBudget =
        this.totalSalary * (this.compensationPercentage / 100) * 12;
    },

    // ============================================================
    // คำนวณค่าตอบแทน (main logic)
    // ============================================================
    calculateCompensation() {
      if (!this.canCalculate) {
        this.showSnack(
          "ไม่พบข้อมูลพนักงานที่มีสิทธิ์ กรุณาโหลดข้อมูลก่อน",
          "warning",
          "mdi-alert"
        );
        return;
      }

      this.calculating = true;

      try {
        const eligible = this.compensationData.filter(e => e.eligible);
        if (eligible.length === 0) {
          this.showSnack(
            "ไม่มีพนักงานที่มีสิทธิ์รับการเลื่อน",
            "warning",
            "mdi-alert"
          );
          return;
        }

        // คะแนนรวมของผู้มีสิทธิ์
        const totalScore = eligible.reduce(
          (s, e) => s + e.performance_score,
          0
        );
        if (totalScore === 0) {
          this.showSnack(
            "ผลรวมคะแนนเป็น 0 ไม่สามารถคำนวณได้",
            "error",
            "mdi-alert"
          );
          return;
        }

        // ===== ขั้นที่ 1-4: คำนวณขั้นต้น =====
        eligible.forEach(emp => {
          // ขั้นที่ 1: สูงสุด 6%
          emp.max_increase_6_percent = parseFloat(
            (emp.salary_s * (this.maxAllowedPercentage / 100)).toFixed(2)
          );

          // ขั้นที่ 2: ร้อยละรายบุคคล (ยังไม่บริหาร) = maxAllowed% × (คะแนนบุคคล / คะแนนรวม)
          emp.individual_percentage_pre_manage = parseFloat(
            (
              (this.maxAllowedPercentage * emp.performance_score) /
              totalScore
            ).toFixed(3)
          );

          // ขั้นที่ 3: เงินเลื่อนขั้นต้น
          emp.initial_increase_pre_manage = parseFloat(
            (
              emp.salary_s *
              (emp.individual_percentage_pre_manage / 100)
            ).toFixed(2)
          );

          // ขั้นที่ 4: ปัดเศษขึ้น 10 บาท
          emp.initial_rounded_pre_manage = roundUpTen(
            emp.initial_increase_pre_manage
          );
        });

        // ===== ขั้นที่ 5: วงเงินที่เหลือสำหรับบริหาร =====
        // วงเงิน/ปี − (ยอดปัดเศษต้น × 12 เดือน)
        const sumInitialRounded = eligible.reduce(
          (s, e) => s + e.initial_rounded_pre_manage,
          0
        );
        const remainingBudgetForMgmt =
          this.totalCompensationBudget - sumInitialRounded * 12;

        // ===== ขั้นที่ 6: ร้อยละบริหาร (เฉลี่ยเท่ากัน/คน) =====
        const eligibleSalarySum = eligible.reduce((s, e) => s + e.salary_s, 0);
        // remaining แบ่งเป็น /เดือน แล้วหาร salary รวม
        const mgmtPctPerPerson = parseFloat(
          ((remainingBudgetForMgmt / 12 / eligibleSalarySum) * 100).toFixed(3)
        );

        // ===== ขั้นที่ 7-8: คำนวณผลสุดท้ายรายบุคคล =====
        eligible.forEach(emp => {
          // ขั้นที่ 6: ร้อยละบริหาร
          emp.management_percentage = mgmtPctPerPerson;

          // ขั้นที่ 7: ร้อยละเลื่อนจริง = รายบุคคล + บริหาร (ไม่เกิน maxAllowed%)
          const rawActualPct =
            emp.individual_percentage_pre_manage + emp.management_percentage;
          emp.actual_percentage_final = parseFloat(
            Math.min(rawActualPct, this.maxAllowedPercentage).toFixed(3)
          );

          // ขั้นที่ 8: เงินเลื่อนจริง
          emp.actual_increase_amount = parseFloat(
            (emp.salary_s * (emp.actual_percentage_final / 100)).toFixed(2)
          );
        });

        // ===== ขั้นที่ 8 ต่อ: ปัดเศษ + Iterative redistribute + เพดานกลุ่มงาน =====
        this.applyRoundingAndBudgetControl(eligible);

        // ===== ขั้นที่ 9: อัปเดต actual_percentage_final ให้สะท้อนผลจริงหลัง redistribute =====
        eligible.forEach(emp => {
          emp.actual_increase_amount = emp.final_rounded;
          emp.actual_percentage_final = parseFloat(
            ((emp.final_rounded / emp.salary_s) * 100).toFixed(3)
          );
        });

        // คืนค่า = 0 สำหรับผู้ไม่มีสิทธิ์
        this.compensationData
          .filter(e => !e.eligible)
          .forEach(emp => {
            emp.max_increase_6_percent = 0;
            emp.individual_percentage_pre_manage = 0;
            emp.initial_increase_pre_manage = 0;
            emp.initial_rounded_pre_manage = 0;
            emp.management_percentage = 0;
            emp.actual_percentage_final = 0;
            emp.actual_increase_amount = 0;
            emp.final_rounded = 0;
            emp.new_salary = emp.salary_s;
            emp.capped = false;
          });

        this.updateStatistics();
        this.calculationComplete = true;
        this.showSnack(
          `✓ คำนวณเสร็จสิ้น มีสิทธิ์ ${
            eligible.length
          } คน วงเงิน ${this.formatCurrency(
            this.totalCompensationDistributed
          )} บาท/ปี`,
          "success",
          "mdi-check-circle"
        );
      } catch (err) {
        this.showSnack(
          "เกิดข้อผิดพลาดในการคำนวณ: " + err.message,
          "error",
          "mdi-alert-circle"
        );
        console.error(err);
      } finally {
        this.calculating = false;
      }
    },

    // ============================================================
    // ปัดเศษและควบคุมวงเงิน (ข้อ 1(5)(6))
    // ============================================================
    // ============================================================
    // ปัดเศษ + ควบคุมวงเงิน + กระจายเงินที่เหลือจากคนถึงเพดาน (Iterative)
    // ============================================================
    applyRoundingAndBudgetControl(eligible) {
      const MAX_ITER = 20;
      const budgetPerMonth = this.totalCompensationBudget / 12;

      // --- Helper: คำนวณ final_rounded + capped ให้ emp 1 คน ---
      const calcFinal = (emp, pct) => {
        const rawInc = emp.salary_s * (pct / 100);
        let inc = roundUpTen(rawInc);
        let newSal = emp.salary_s + inc;
        let capped = false;
        const maxSal = this.getGroupMax(emp.name_type_group);
        if (newSal > maxSal) {
          // ปัดเศษขึ้น 10 บาทของส่วนที่รับได้จริง ข้อ 1(6)
          inc = roundUpTen(Math.max(0, maxSal - emp.salary_s));
          // แต่ถ้าเกิน maxSal แล้ว ไม่ต้องปัดขึ้นอีก → ใช้ floor ถึง maxSal
          inc = Math.min(inc, maxSal - emp.salary_s);
          newSal = emp.salary_s + inc;
          capped = true;
        }
        return { inc, newSal, capped };
      };

      // ===== Iterative Redistribution =====
      // รอบที่ 1: คำนวณด้วย actual_percentage_final ที่มี
      // รอบต่อๆ ไป: นำเงินที่เหลือจากคนถึงเพดานกระจายให้คนที่ยังไม่ถึง
      // จนกว่าวงเงินจะถูกใช้ครบหรือไม่มีการเปลี่ยนแปลง

      // ตั้งค่าเริ่มต้น
      eligible.forEach(emp => {
        const r = calcFinal(emp, emp.actual_percentage_final);
        emp.final_rounded = r.inc;
        emp.new_salary = r.newSal;
        emp.capped = r.capped;
      });

      for (let iter = 0; iter < MAX_ITER; iter++) {
        const usedMo = eligible.reduce((s, e) => s + e.final_rounded, 0);
        const remainMo = budgetPerMonth - usedMo;

        // ถ้าเหลือน้อยกว่า 10 บาท หรือไม่มีคนที่ยังไม่ถึงเพดาน → หยุด
        if (remainMo < 10) break;
        const nonCapped = eligible.filter(e => !e.capped);
        if (nonCapped.length === 0) break;

        // กระจายเงินที่เหลือ (remainMo) ให้คนที่ยังไม่ถึงเพดาน
        // โดยใช้สัดส่วนเงินเดือน (proportional distribution)
        const nonCappedSalary = nonCapped.reduce((s, e) => s + e.salary_s, 0);
        let redistributed = 0;

        nonCapped.forEach(emp => {
          // หา % เพิ่มที่คนนี้ควรได้จาก remainMo
          const addMo = remainMo * (emp.salary_s / nonCappedSalary);
          // ปัดเศษ 10 บาท
          const addRounded = roundUpTen(addMo);
          const tryInc = emp.final_rounded + addRounded;
          const tryNewSal = emp.salary_s + tryInc;
          const maxSal = this.getGroupMax(emp.name_type_group);

          if (tryNewSal <= maxSal) {
            emp.final_rounded = tryInc;
            emp.new_salary = tryNewSal;
            redistributed += addRounded;
          } else {
            // ถึงเพดานระหว่าง redistribute
            const capInc = Math.max(0, maxSal - emp.salary_s);
            redistributed += capInc - emp.final_rounded;
            emp.final_rounded = capInc;
            emp.new_salary = maxSal;
            emp.capped = true;
          }
        });

        // ถ้าไม่มีการกระจายได้เลย → หยุด (ทุกคนถึงเพดานแล้ว)
        if (redistributed <= 0) break;
      }

      // กรณีที่ยังเกินวงเงินหลัง redistribute → ลดรายที่ได้มากสุดทีละ 10 (ข้อ 1(5))
      let totalYearFinal = eligible.reduce(
        (s, e) => s + e.final_rounded * 12,
        0
      );
      let cutIter = 0;
      while (totalYearFinal > this.totalCompensationBudget && cutIter < 500) {
        const maxEmp = eligible
          .filter(e => !e.capped && e.final_rounded >= 10)
          .reduce((m, c) => (c.final_rounded > m.final_rounded ? c : m), {
            final_rounded: -1
          });
        if (maxEmp.final_rounded < 0) break;
        maxEmp.final_rounded -= 10;
        maxEmp.new_salary -= 10;
        totalYearFinal -= 120;
        cutIter++;
      }
    },

    // ============================================================
    // อัปเดต statistics หลังคำนวณ
    // ============================================================
    updateStatistics() {
      this.totalCompensationDistributed = this.totalFinalRounded * 12;
      this.remainingBudget =
        this.totalCompensationBudget - this.totalCompensationDistributed;
    },

    // ============================================================
    // อนุมัติ
    // ============================================================
    approveCompensation() {
      if (!this.canApprove) {
        this.showSnack(
          "ไม่สามารถอนุมัติได้ กรุณาตรวจสอบการคำนวณและวงเงิน",
          "error",
          "mdi-alert"
        );
        return;
      }
      this.approvalDialog = true;
    },

    async confirmApproval() {
      this.saving = true;
      try {
        const eligible = this.compensationData.filter(e => e.eligible);
        const res = await this.$http.post("compensation_approve.php", {
          ApiKey: this.ApiKey,
          year: this.selectedYear,
          college_code: this.user.user_code,
          total_budget: this.totalCompensationBudget,
          total_distributed: this.totalCompensationDistributed,
          percentage: this.compensationPercentage,
          compensation_data: eligible.map(e => ({
            id_card: e.id_card,
            performance_score: e.performance_score,
            individual_percentage_pre_manage:
              e.individual_percentage_pre_manage,
            initial_increase_pre_manage: e.initial_increase_pre_manage,
            initial_rounded_pre_manage: e.initial_rounded_pre_manage,
            management_percentage: e.management_percentage,
            actual_percentage_final: e.actual_percentage_final,
            actual_increase_amount: e.actual_increase_amount,
            final_rounded: e.final_rounded,
            salary_s: e.salary_s,
            new_salary: e.new_salary
          }))
        });

        if (res.data.status) {
          this.showSnack(
            `✓ อนุมัติเรียบร้อย พนักงาน ${res.data.total_employees} คน ` +
              `วงเงิน ${this.formatCurrency(res.data.total_amount)} บาท/เดือน`,
            "success",
            "mdi-check-circle"
          );
          this.approvalDialog = false;
          // reload เพื่อแสดงสถานะล่าสุด
          await this.loadAllData();
        } else {
          this.showSnack(
            res.data.message || "เกิดข้อผิดพลาดในการอนุมัติ",
            "error",
            "mdi-alert"
          );
        }
      } catch (err) {
        this.showSnack("บันทึกไม่สำเร็จ: " + err.message, "error", "mdi-alert");
      } finally {
        this.saving = false;
      }
    },

    // ============================================================
    // Export CSV
    // ============================================================
    exportCSV() {
      const headers = [
        "ลำดับ",
        "เลขบัตรประชาชน",
        "ชื่อ-สกุล",
        "ตำแหน่ง",
        "กลุ่มงาน",
        "ค่าตอบแทนปัจจุบัน",
        "คะแนนประเมิน",
        "ระดับ",
        "มีสิทธิ์",
        "หมายเหตุ",
        "วันลานับ",
        "มาสาย",
        "ขาดราชการ",
        "% รายบุคคล(ก่อนบริหาร)",
        "เลื่อนขั้นต้น",
        "ปัดเศษต้น",
        "% บริหารวงเงิน",
        "% เลื่อนจริง",
        "เลื่อนจริง",
        "ปัดเศษจริง",
        "ค่าตอบแทนใหม่"
      ];
      const rows = this.compensationData.map((e, i) => [
        i + 1,
        e.id_card,
        e.full_name,
        e.name_postion,
        e.name_type_group,
        e.salary_s,
        e.performance_score,
        gradeLabel(e.performance_score),
        e.eligible ? "มีสิทธิ์" : "ไม่มีสิทธิ์",
        e.ineligible_reason || "",
        e.total_counted_leave,
        e.late_count,
        e.absent_count,
        e.individual_percentage_pre_manage,
        e.initial_increase_pre_manage,
        e.initial_rounded_pre_manage,
        e.management_percentage,
        e.actual_percentage_final,
        e.actual_increase_amount,
        e.final_rounded,
        e.new_salary
      ]);
      const csv = [headers, ...rows]
        .map(r => r.map(v => `"${v}"`).join(","))
        .join("\n");
      const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8;"
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `compensation_${this.selectedYear}_round${this.assessmentRound}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      this.showSnack("ส่งออก CSV เรียบร้อย", "info", "mdi-file-export");
    },

    // ============================================================
    // เริ่มใหม่
    // ============================================================
    resetAll() {
      if (!confirm("ต้องการเริ่มต้นใหม่? ข้อมูลการคำนวณปัจจุบันจะหายไป"))
        return;
      this.compensationData = [];
      this.personnelList = [];
      this.attendanceList = [];
      this.calculationComplete = false;
      this.totalPersonnel = 0;
      this.totalSalary = 0;
      this.totalCompensationBudget = 0;
      this.totalCompensationDistributed = 0;
      this.remainingBudget = 0;
      this.showSnack("เริ่มต้นใหม่เรียบร้อย", "info", "mdi-information");
    },

    // ============================================================
    // ดูรายละเอียด
    // ============================================================
    viewDetails(item) {
      this.selectedEmployee = item;
      this.detailDialog = true;
    }
  }
};
</script>

<style scoped>
/* ===== ตาราง ===== */
.calc-table >>> .v-data-table__wrapper {
  overflow-x: auto;
}
.calc-table >>> .v-data-footer {
  border-top: 2px solid #e0e0e0;
}

/* ===== Summary Card ===== */
.summary-card {
  border-radius: 10px !important;
}

.summary-item {
  background: #fff;
  border-radius: 8px;
  padding: 14px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  border-left: 4px solid #455a64;
  transition: transform 0.2s, box-shadow 0.2s;
}
.summary-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
}
.summary-label {
  font-size: 11px;
  font-weight: 600;
  color: #888;
  margin: 0 0 6px 0;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.summary-value {
  font-size: 22px;
  font-weight: bold;
  margin: 6px 0;
}
.summary-unit {
  font-size: 11px;
  color: #aaa;
  margin: 3px 0 0 0;
}

/* ===== Info Card ===== */
.info-card {
  background: #f8f9fa !important;
  border: 1px solid #e9ecef !important;
  border-left: 4px solid #455a64 !important;
  transition: background 0.2s;
}
.info-card:hover {
  background: #f5f5f5 !important;
}
.info-title {
  font-weight: 600;
  font-size: 12px;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 0;
}
.info-value {
  font-size: 24px;
  font-weight: bold;
  margin: 10px 0 0 0;
}

/* ===== Color overrides ===== */
.purple--text {
  color: #b3151b !important;
}
.indigo--text {
  color: #b3151b !important;
}
.teal--text {
  color: #263238 !important;
}
.cyan--text {
  color: #263238 !important;
}
.deep-orange--text {
  color: #e64a19 !important;
}

/* ===== code tag ===== */
code {
  background: #f0f0f0;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 0.82em;
  color: #c62828;
}

/* ===== Responsive ===== */
@media (max-width: 600px) {
  .summary-item {
    padding: 10px;
  }
  .summary-value {
    font-size: 16px;
  }
  .info-value {
    font-size: 18px;
  }
}
</style>
